<script lang="ts" setup>
const { t } = useI18n()
const { modals, toggleModal } = useOverlays()
const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const listsStore = useListsStore()
const api = useApi()
const localePath = useLocalePath()
const router = useRouter()

const newList = ref<Partial<List>>(getDefaultList())
const creating = ref(false)
const count = ref(0)

const quotes = computed(() => {
  if (modals.list.title) {
    return [modals.list.title]
  }

  return [
    t('title'),
    t('companies'),
    listsStore.lists.length ? '' : t('firstList'),
  ]
})

watch(
  () => newList.value.query,
  async () => {
    try {
      await updateQueryCount()
    }
    catch {}
  },
  {
    deep: true,
    immediate: true,
  },
)

function getDefaultList() {
  return {
    dynamic: false,
    maxCompanies: 0,
    name: t('defaultListName'),
    query: [],
  }
}

const updateQueryCount = useDebounceFn(
  async () => {
    if (!(newList.value?.query as SegmentationCondition[] || []).find(condition => condition.values.length)) {
      count.value = 0

      return
    }

    const countResponse = await api.companies.count({ query: newList.value.query })

    count.value = countResponse.data.count
  },
  1000,
)

function onClose() {
  newList.value = getDefaultList()
}

async function onListCreate(importCompanies = false) {
  creating.value = true

  try {
    const list = await listsStore.createList({
      dynamic: newList.value.dynamic as boolean,
      maxCompanies: newList.value.maxCompanies as number,
      name: newList.value.name as string,
      query: newList.value.query as SegmentationCondition[],
    })

    if (importCompanies && list.id) {
      await listsStore.toggleCompaniesFromList({
        listId: list.id,
        query: newList.value.query as SegmentationCondition[],
      })
    }

    createToast({ message: 'listCreated' })
    createEvent({ name: '[Lists] List created' })

    newList.value = getDefaultList()

    toggleModal({
      opened: false,
      type: 'list',
    })

    router.push({
      path: localePath({
        name: 'companies-lists-listId',
        params: { listId: `${list.id}` },
      }),
    })
  }
  catch (error) {
    console.error(error)
  }

  creating.value = false
}
</script>

<template>
  <UModal
    :prevent-close="!modals.list.closable"
    :ui="{ width: 'sm:max-w-2xl md:max-w-3xl' }"
    @close="onClose"
  >
    <UCard
      :ui="{
        body: {
          base: 'grid max-h-[55vh] min-h-[55vh] overflow-y-auto py-10',
          padding: 'p-6 md:p-8',
        },
        header: {
          base: 'p-6 md:p-8',
        },
      }"
    >
      <template #header>
        <BaseQuote
          character="power"
          size="sm"
        >
          <p
            v-for="(item, index) in quotes"
            :key="index"
          >
            {{ item }}
          </p>
        </BaseQuote>
      </template>

      <ListSettings
        v-model="newList"
        show-name
        show-query
      />

      <template #footer>
        <div class="flex w-full items-center gap-4">
          <UButton
            block
            class="w-full flex-1"
            :icon="getIcon('save')"
            :label="t('create')"
            :loading="creating"
            size="lg"
            type="submit"
            :ui="{ icon: { base: 'text-green-400 dark:text-green-600' } }"
            @click="onListCreate(false)"
          />

          <UButton
            v-if="newList?.query?.length && count"
            block
            class="w-full flex-1"
            :icon="getIcon('import')"
            :label="`${t('globals.saveAndImport')}${count ? ` (${numbro(count <= (newList?.maxCompanies || Number.MAX_SAFE_INTEGER) ? count : newList.maxCompanies || 0)})` : ''}`"
            :loading="creating"
            size="lg"
            :ui="{ icon: { base: 'text-teal-400 dark:text-teal-600' } }"
            @click="onListCreate(true)"
          />
        </div>
      </template>
    </UCard>
  </UModal>
</template>

<i18n lang="json">
{
  "en": {
    "companies": "Companies saved in lists are free to retrieve.",
    "create": "Create the list",
    "defaultListName": "My new list",
    "firstList": "It looks like you're creating your first list. Let me guide you through the process.",
    "title": "Lists help you organize and save groups of companies."
  },
  "es": {
    "companies": "Las empresas guardadas en listas son gratuitas de recuperar.",
    "create": "Crear la lista",
    "defaultListName": "Mi nueva lista",
    "firstList": "Parece que estás creando tu primera lista. Deja que te guíe.",
    "title": "Las listas te permiten organizar y guardar grupos de empresas."
  },
  "fr": {
    "companies": "Les entreprises enregistrées dans les listes sont gratuites à récupérer.",
    "create": "Créer la liste",
    "defaultListName": "Ma nouvelle liste",
    "firstList": "Il semble que vous créez votre première liste. Laissez-moi vous guider.",
    "title": "Les listes vous permettent d'organiser et de sauvegarder des groupes d'entreprises."
  }
}
</i18n>
